import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Crown, Copy, Loader2, Link2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { RoleBadge, StatusBadge, HealthDot } from "@/components/Badges";
import { getExchange } from "@/data/exchanges";
import { fmtUsd, relTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const ROLE_FILTERS = [
  { value: "", label: "All" },
  { value: "master", label: "Masters" },
  { value: "copy", label: "Connected" },
];
const STATUS_FILTERS = [
  { value: "", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "unverified", label: "Unverified" },
  { value: "inactive", label: "Inactive" },
];

function ExchangeAvatar({ slug, role }) {
  const ex = getExchange(slug);
  return (
    <div className="relative w-11 h-11 rounded-md border border-[#262626] bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
      <span className="font-heading font-bold text-sm" style={{ color: ex.color }}>{ex.name.slice(0, 2).toUpperCase()}</span>
      <span className={cn("absolute -bottom-1.5 -right-1.5 w-5 h-5 rounded-full border-2 border-[#141414] flex items-center justify-center",
        role === "master" ? "bg-[#D4AF37]" : "bg-[#333333]")}>
        {role === "master" ? <Crown className="w-2.5 h-2.5 text-[#0A0A0A]" /> : <Copy className="w-2.5 h-2.5 text-[#F5F5F5]" />}
      </span>
    </div>
  );
}

export default function Exchanges() {
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ["accounts", role, status],
    queryFn: async () => {
      const params = {};
      if (role) params.role = role;
      if (status) params.status = status;
      return (await api.get("/exchange-accounts", { params })).data;
    },
  });

  const filtered = useMemo(() => {
    if (!q.trim()) return accounts;
    const s = q.toLowerCase();
    return accounts.filter((a) => a.account_name?.toLowerCase().includes(s) || a.exchange_name?.toLowerCase().includes(s) || a.master_name?.toLowerCase().includes(s));
  }, [accounts, q]);

  return (
    <div>
      <PageHeader
        title="My Exchanges"
        subtitle="Master and connected accounts across all connected exchanges."
        pageKey="exchanges"
        testid="exchanges-header"
        actions={
          <button onClick={() => navigate("/exchanges/new")} data-testid="exchange-add-btn"
            className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F0C74B] text-[#0A0A0A] font-semibold text-sm rounded-sm px-4 py-2 transition-colors">
            <Plus className="w-4 h-4" /> Add Account
          </button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts..."
            data-testid="exchange-search"
            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-sm pl-9 pr-3 py-2 text-sm text-[#F5F5F5] placeholder:text-[#525252] focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
        </div>
        <div className="flex items-center gap-1 bg-[#0A0A0A] border border-[#333333] rounded-sm p-1">
          {ROLE_FILTERS.map((f) => (
            <button key={f.value} onClick={() => setRole(f.value)} data-testid={`filter-role-${f.value || "all"}`}
              className={cn("px-3 py-1.5 text-xs rounded-sm transition-colors", role === f.value ? "bg-[#D4AF37] text-[#0A0A0A] font-medium" : "text-[#A3A3A3] hover:text-[#F5F5F5]")}>
              {f.label}
            </button>
          ))}
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status"
          className="bg-[#0A0A0A] border border-[#333333] rounded-sm px-3 py-2 text-sm text-[#F5F5F5] focus:outline-none focus:border-[#D4AF37]">
          {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#333333] bg-[#0D0D0D] p-16 text-center" data-testid="exchanges-empty">
          <Link2 className="w-8 h-8 text-[#525252] mx-auto mb-3" />
          <div className="text-[#F5F5F5] font-medium">No accounts found</div>
          <p className="text-sm text-[#737373] mt-1">Add your first exchange account to get started.</p>
        </div>
      ) : (
        <div className="rounded-md border border-[#262626] bg-[#141414] overflow-hidden" data-testid="exchanges-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626] text-left">
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-3">Account</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-3">Type</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-3">Relationship</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-3">Market</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-3 text-right">Balance</th>
                <th className="text-[10px] uppercase tracking-wider text-[#737373] font-medium px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <motion.tr
                  key={a.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  onClick={() => navigate(`/exchanges/${a.id}`)}
                  data-testid={`exchange-row-${a.id}`}
                  className="border-b border-[#1c1c1c] last:border-0 hover:bg-[#1A1A1A] cursor-pointer group"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <ExchangeAvatar slug={a.exchange_slug} role={a.account_role} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#F5F5F5] truncate max-w-[220px]">{a.account_name}</div>
                        <div className="text-[11px] text-[#737373]">{a.exchange_name} · {a.api_key_masked}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={a.account_role} /></td>
                  <td className="px-4 py-3 text-xs text-[#A3A3A3]">
                    {a.account_role === "master"
                      ? <span className="flex items-center gap-1.5"><Copy className="w-3 h-3 text-[#525252]" />{a.copy_count ?? 0} connected</span>
                      : <span className="flex items-center gap-1.5"><Crown className="w-3 h-3 text-[#D4AF37]" /><span className="truncate max-w-[160px]">{a.master_name || "—"}</span></span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A3A3A3] capitalize">{a.market}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm text-[#F5F5F5]">{a.balance != null ? fmtUsd(a.balance) : "—"}</td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><HealthDot status={a.connection_status === "unverified" ? "unverified" : a.status} /><StatusBadge status={a.connection_status === "unverified" ? "unverified" : a.status} /></div></td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-[#525252] group-hover:text-[#D4AF37] transition-colors ml-auto" /></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
